import * as React from "react"
import { router } from "@inertiajs/react"
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from "@/components/ui/Card"
import { Button } from "@/components/ui/Button"
import { Input } from "@/components/ui/Input"
import { Badge } from "@/components/ui/Badge"
import { Separator } from "@/components/ui/Separator"
import { admin_integrations_slack_path, admin_integrations_slack_test_path } from "@/lib/routes"

export interface SlackState {
  connected: boolean
  enabled?: boolean
  workspace_name?: string | null
  default_channel?: string | null
  connected_by?: string | null
  connected_at?: string | null
}

export default function SlackIntegrationCard({ slack }: { slack: SlackState }) {
  const [token, setToken] = React.useState("")
  const [channel, setChannel] = React.useState(slack.default_channel ?? "")
  const [busy, setBusy] = React.useState(false)

  const visit = (opts: Parameters<typeof router.patch>[2] = {}) => ({
    preserveScroll: true,
    onStart: () => setBusy(true),
    onFinish: () => setBusy(false),
    ...opts,
  })

  function connect(e: React.FormEvent) {
    e.preventDefault()
    if (!token.trim()) return
    router.patch(
      admin_integrations_slack_path(),
      { token: token.trim(), default_channel: channel.trim() || null },
      visit({ onSuccess: () => setToken("") }),
    )
  }

  const sendTest = () => router.post(admin_integrations_slack_test_path(), {}, visit())
  const saveChannel = (value: string) =>
    router.patch(admin_integrations_slack_path(), { default_channel: value }, visit())
  const toggle = () => router.patch(admin_integrations_slack_path(), { enabled: !slack.enabled }, visit())
  function disconnect() {
    if (!window.confirm("Disconnect Slack? Atlas will stop being able to send messages.")) return
    router.delete(admin_integrations_slack_path(), visit())
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-3">
          <div className="space-y-1.5">
            <CardTitle className="text-lg">Slack</CardTitle>
            <CardDescription>
              Send planning notifications to your team in Slack.
            </CardDescription>
          </div>
          {slack.connected ? (
            <Badge variant={slack.enabled ? "success" : "warning"}>
              {slack.enabled ? "Connected" : "Paused"}
            </Badge>
          ) : (
            <Badge variant="outline">Not connected</Badge>
          )}
        </div>
      </CardHeader>

      {slack.connected ? (
        <>
          <CardContent className="space-y-2 text-sm">
            <Row label="Workspace" value={slack.workspace_name ?? "—"} />
            <Row
              label="Connected"
              value={[slack.connected_by, formatDate(slack.connected_at)].filter(Boolean).join(" · ") || "—"}
            />
            <DefaultChannelField saved={slack.default_channel ?? ""} busy={busy} onSave={saveChannel} />
          </CardContent>
          <Separator />
          <CardFooter className="flex flex-wrap gap-2 pt-6">
            <Button variant="outline" onClick={sendTest} disabled={busy}>
              Send test message
            </Button>
            <Button variant="outline" onClick={toggle} disabled={busy}>
              {slack.enabled ? "Pause" : "Resume"}
            </Button>
            <Button variant="ghost" onClick={disconnect} disabled={busy} className="ml-auto text-destructive">
              Disconnect
            </Button>
          </CardFooter>
        </>
      ) : (
        <CardContent>
          <form onSubmit={connect} className="space-y-3">
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">Bot User OAuth Token</label>
              <Input
                type="password"
                autoComplete="off"
                placeholder="xoxb-…"
                value={token}
                onChange={(e) => setToken(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                From your Slack app's OAuth &amp; Permissions page. Required scopes:{" "}
                <code className="text-foreground">chat:write</code>,{" "}
                <code className="text-foreground">users:read</code>,{" "}
                <code className="text-foreground">users:read.email</code>.
              </p>
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">
                Default channel <span className="text-muted-foreground">(optional)</span>
              </label>
              <Input
                placeholder="#planning"
                value={channel}
                onChange={(e) => setChannel(e.target.value)}
              />
            </div>
            <Button type="submit" disabled={busy || !token.trim()}>
              Connect
            </Button>
          </form>
        </CardContent>
      )}
    </Card>
  )
}

// Editable after connecting so the channel can change without re-pasting the
// bot token. Save only lights up when the value differs from what is stored.
function DefaultChannelField({
  saved,
  busy,
  onSave,
}: {
  saved: string
  busy: boolean
  onSave: (value: string) => void
}) {
  const [value, setValue] = React.useState(saved)
  React.useEffect(() => setValue(saved), [saved])
  const dirty = value.trim() !== saved

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        if (dirty) onSave(value.trim())
      }}
      className="space-y-1.5 pt-2"
    >
      <label className="text-sm font-medium text-foreground">Default channel</label>
      <div className="flex gap-2">
        <Input placeholder="#planning" value={value} onChange={(e) => setValue(e.target.value)} />
        <Button type="submit" variant="outline" disabled={busy || !dirty}>
          Save
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">
        Where Atlas posts alerts, such as a failed nightly data sync. Invite the Atlas bot to this
        channel so it can post there.
      </p>
    </form>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-4">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-foreground text-right">{value}</span>
    </div>
  )
}

function formatDate(iso?: string | null): string {
  if (!iso) return ""
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString()
}
